import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../icons';
import { Popover } from './popover';
import { Select, type SelectOption } from './select';
import { tabListKeyDown, tabProps } from './tabs';

// --- Segmented control ------------------------------------------------------------------

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** A count rendered after the label, e.g. "Active 3". */
  count?: number | string;
  icon?: IconName;
  /** Needed when `label` is not plain text (an icon-only segment). */
  ariaLabel?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name of the group. */
  label: string;
  /**
   * `contrast`: a white track with a dark selected segment — page-level
   * filters (date range, status tabs). `soft`: a sunken track with a raised
   * white segment — choices inside a form (role, format, type).
   */
  tone?: 'contrast' | 'soft';
  /** `md` sits beside a page title; `lg` matches a 42px input row. */
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  /** Render as tabs that switch one panel, instead of a radio group. */
  tabs?: { idPrefix: string; panelId: string };
  className?: string;
}

const SEGMENT_HEIGHT = { sm: 'h-7', md: 'h-[30px]', lg: 'h-9' } as const;

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  tone = 'contrast',
  size = 'md',
  fullWidth = false,
  tabs,
  className = '',
}: SegmentedControlProps<T>) {
  const values = options.map((o) => o.value);
  const track =
    tone === 'contrast'
      ? 'rounded-[11px] bg-(--color-surface) ring-1 ring-(--color-line)'
      : 'rounded-[10px] bg-(--color-surface-sunken)';
  return (
    <div
      role={tabs ? 'tablist' : 'radiogroup'}
      aria-label={label}
      onKeyDown={tabListKeyDown(values, value, onChange)}
      className={`${fullWidth ? 'flex' : 'inline-flex'} max-w-full gap-0.5 overflow-x-auto p-[3px] ${track} ${className}`}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const roleProps = tabs
          ? tabProps(option.value, value, tabs.idPrefix, tabs.panelId)
          : { role: 'radio' as const, 'aria-checked': selected, 'data-tab': option.value, tabIndex: selected ? 0 : -1 };
        const look =
          tone === 'contrast'
            ? selected
              ? 'bg-(--color-ink-900) text-white'
              : 'text-(--color-ink-500) hover:text-(--color-ink-900)'
            : selected
              ? 'bg-(--color-surface) text-(--color-ink-900) shadow-segment'
              : 'text-(--color-ink-500) hover:text-(--color-ink-900)';
        return (
          <button
            key={option.value}
            type="button"
            {...roleProps}
            aria-label={option.ariaLabel}
            onClick={() => onChange(option.value)}
            className={`touch-target inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-[11px] text-[12.5px] font-semibold transition-colors ${
              tone === 'contrast' ? 'rounded-lg' : 'rounded-[7px]'
            } ${SEGMENT_HEIGHT[size]} ${fullWidth ? 'flex-1' : ''} ${look}`}
          >
            {option.icon ? <Icon name={option.icon} className="size-[15px]" /> : null}
            {option.label}
            {option.count !== undefined ? (
              <span className="tabular text-[11px] font-bold opacity-60">{option.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// --- Switch -----------------------------------------------------------------------------

/**
 * An on/off toggle. The label is the accessible name; render visible text
 * beside it when the context needs one. Its state should change the moment it
 * is pressed — callers update optimistically and roll back on error.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  id,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`touch-target inline-flex h-5 w-[34px] shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-(--color-accent)' : 'bg-(--color-ink-300)'
      }`}
    >
      <span
        aria-hidden
        className={`size-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform duration-200 ${
          checked ? 'translate-x-3.5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// --- Filter pill ------------------------------------------------------------------------

interface FilterPillBase {
  /** The dimension, e.g. "Tag". Shown on the empty pill. */
  label: string;
  icon?: IconName;
}

interface FilterPillOptions extends FilterPillBase {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Text of the active pill; defaults to "{label} is {option}". */
  describe?: (option: SelectOption) => string;
}

interface FilterPillPanel extends FilterPillBase {
  /** A free-form editor for filters a single choice cannot express (a field condition). */
  panel: (close: () => void) => ReactNode;
  /** Text of the active pill, or null when the filter is off. */
  activeLabel: string | null;
  onClear: () => void;
  /** A solid trigger while off, to sit with solid selects (Contacts, R10) rather than as a dashed "add filter". */
  solid?: boolean;
}

export type FilterPillProps = FilterPillOptions | FilterPillPanel;

const ACTIVE_PILL =
  'inline-flex h-[38px] max-w-full items-center rounded-[10px] bg-(--color-accent-wash) pl-1 pr-1 text-(--color-accent-strong) ring-1 ring-(--color-accent-line)';

function ClearButton({ label, onClear }: { label: string; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={t('filterPill.remove', { label })}
      onClick={onClear}
      className="touch-target flex size-6 shrink-0 items-center justify-center rounded-md text-(--color-accent) transition-colors hover:bg-white/70"
    >
      <Icon name="x" className="size-[13px]" />
    </button>
  );
}

/**
 * One filter in a filter bar: a dashed "add filter" pill while off, a solid
 * violet pill with × once on. Replaces the stacked full-width selects.
 */
export function FilterPill(props: FilterPillProps) {
  if ('options' in props) return <OptionsFilterPill {...props} />;
  return <PanelFilterPill {...props} />;
}

function OptionsFilterPill({ label, icon, options, value, onChange, describe }: FilterPillOptions) {
  const { t } = useTranslation();
  const filterBy = t('filterPill.filterBy', { label: label.toLowerCase() });
  const selected = options.find((o) => o.value === value);
  if (!selected) {
    return (
      <Select
        variant="filter"
        icon={icon}
        value=""
        placeholder={label}
        displayLabel={label}
        options={options}
        onChange={(e) => onChange(e.target.value)}
        aria-label={filterBy}
        className="w-auto"
      />
    );
  }
  return (
    <span className={ACTIVE_PILL}>
      <Select
        variant="bare"
        value={selected.value}
        displayLabel={describe ? describe(selected) : t('filterPill.is', { label, value: selected.label })}
        options={options}
        onChange={(e) => onChange(e.target.value)}
        aria-label={filterBy}
        className="w-auto"
      />
      <ClearButton label={label} onClear={() => onChange(null)} />
    </span>
  );
}

/** What takes focus when a panel opens: its checked choice, else its first control. */
const PANEL_FIRST_FOCUS = '[aria-checked="true"], button:not([disabled]):not([tabindex="-1"]), input, [tabindex="0"]';

function PanelFilterPill({ label, icon, panel, activeLabel, onClear, solid = false }: FilterPillPanel) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // The panel is portalled to the end of the page: focus goes in when it opens,
  // and back to the pill when it closes from inside (Escape, Apply).
  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>(PANEL_FIRST_FOCUS)?.focus();
  }, [open]);
  const close = () => {
    if (panelRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
    setOpen(false);
  };
  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => setOpen((o) => !o)}
      className={
        activeLabel
          ? 'flex h-8 min-w-0 items-center gap-1.5 truncate rounded-md px-2 text-[12.5px] font-semibold'
          : solid
            ? 'flex min-h-[42px] items-center gap-2 whitespace-nowrap rounded-(--radius-control) bg-(--color-surface) px-3.5 text-[13.5px] text-(--color-ink-900) ring-1 ring-(--color-line-control) transition-shadow hover:ring-(--color-ink-300) aria-expanded:ring-2 aria-expanded:ring-(--color-accent)'
            : 'flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-[10px] border border-dashed border-(--color-ink-300) bg-(--color-surface) px-3 text-[12.5px] font-semibold text-(--color-ink-700) transition-colors hover:border-(--color-accent) hover:text-(--color-ink-900)'
      }
    >
      {icon && !activeLabel && !solid ? <Icon name={icon} className="size-[14px] shrink-0" /> : null}
      <span className="truncate">{activeLabel ?? label}</span>
      {solid && !activeLabel ? <Icon name="chevronDown" className="size-4 shrink-0 text-(--color-ink-500)" /> : null}
    </button>
  );
  return (
    <>
      {activeLabel ? (
        <span className={ACTIVE_PILL}>
          {trigger}
          <ClearButton label={label} onClear={onClear} />
        </span>
      ) : (
        trigger
      )}
      <Popover anchorRef={triggerRef} open={open} onClose={close} width={280} role="dialog" aria-label={t('filterPill.panel', { label })} className="p-3">
        <div ref={panelRef}>{panel(close)}</div>
      </Popover>
    </>
  );
}
